import { makeid } from 'utils/helpers/makeid';

import {
  nurseContactWithPatientItem,
  nurseDecisionMakingNextStepsItem,
  nurseDigitalEngagementEducationMethodItem,
  nurseEmergencyCareAdvisedItem,
  nurseFollowUpPlanItem,
  nurseMaTasksDelegatedItem,
  nurseNoCallMadeRationaleItem,
  nursePatientAcknowledgementItem,
  nursePatientAgreeabilityItem,
  nurseReviewEscalationProtocolItem,
} from '../common';

import type { TFormItem } from '../../types';

export const glucoseClinicalAssessmentList = [
  { title: 'Loss of consciousness/confusion', name: 'lossOfConsciousness' },
  { title: 'Seizure', name: 'seizure' },
  {
    title: 'Inability to eat/drink',
    name: 'inabilityToEatDrink',
  },
  {
    title: 'Persistent vomiting or diarrhea',
    name: 'persistentVomitingDiarrhea',
  },
  { title: 'Shortness of breath', name: 'shortnessOfBreath' },
  {
    title: 'Severe abdominal pain',
    name: 'severeAbdominalPain',
  },
  { title: 'Profound weakness, unable to stand', name: 'profoundWeakness' },
  {
    title: 'Shakiness/sweating',
    name: 'shakinessSweating',
  },
  { title: 'Blurred vision', name: 'blurredVision' },
  { title: 'Extreme thirst/hunger', name: 'extremeThirstHunger' },
  { title: 'Frequent urination', name: 'frequentUrination' },
  {
    title: 'Adherence to diabetes meds/insulin',
    name: 'adherenceToDiabetesMedsInsulin',
  },
  {
    title: 'Sick day or dietary change',
    name: 'sickDayOrDietaryChange',
  },
];

const yesNoClinicalAssessment = [
  'adherenceToDiabetesMedsInsulin',
  'sickDayOrDietaryChange',
];

const clinicalAssessmentItems: TFormItem[] = glucoseClinicalAssessmentList.map(
  (item) => {
    const presentDeniedOptions = [
      { label: 'Present', value: true, key: makeid(10) },
      { label: 'Denied', value: false, key: makeid(10) },
    ];
    const yesNoOptions = [
      { label: 'Yes (describe)', value: true, key: makeid(10) },
      { label: 'No', value: false, key: makeid(10) },
    ];
    return {
      key: makeid(10),
      title: <p>{item.title}:</p>,
      type: 'radio',
      name: item.name,
      required: false,
      options: yesNoClinicalAssessment.includes(item.name)
        ? yesNoOptions
        : presentDeniedOptions,
    };
  },
);

export const glucose: TFormItem[] = [
  {
    key: makeid(10),
    title: <p>Chart Review: At-risk trend summary:</p>,
    type: 'empty',
    name: '_empty',
    items: [
      {
        key: makeid(10),
        title: (
          <p>
            Highest glucose: <span>__ mg/dL</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestGlucose',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest glucose: <span>__ mg/dL</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestGlucose',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: <p>Readings are trending:</p>,
        type: 'radio',
        required: false,
        name: 'typeofTrend',
        options: [
          {
            label: 'High',
            value: 'high',
            key: makeid(10),
          },
          {
            label: 'Low',
            value: 'low',
            key: makeid(10),
          },
          {
            label: 'Both',
            value: 'high&low',
            key: makeid(10),
          },
          {
            label: 'Fluctuating',
            value: 'fluctuating',
            key: makeid(10),
          },
        ],
      },
      {
        key: makeid(10),
        title: null,
        name: 'typeofTrendComment',
        type: 'textarea',
      },
    ],
  },
  { ...nurseContactWithPatientItem },
  { ...nurseNoCallMadeRationaleItem },
  {
    key: makeid(10),
    title: <p>Clinical Assessment</p>,
    type: 'empty',
    name: '_empty',
    items: [
      ...clinicalAssessmentItems,
      {
        key: makeid(10),
        title: <p>Other acute concerns:</p>,
        type: 'textarea',
        name: 'clinicalAssessmentComment',
      },
    ],
  },
  { ...nurseReviewEscalationProtocolItem },
  { ...nurseEmergencyCareAdvisedItem },
  {
    key: makeid(10),
    title: <p>Potential contributing factors:</p>,
    type: 'checkbox',
    required: false,
    name: 'potentialContributingFactors',
    options: [
      {
        label: 'Missed/skipped medication(s)',
        value: 'missed/skipped medication(s)',
        key: makeid(10),
      },
      {
        label: 'Medication administration error',
        value: 'medication administration error',
        key: makeid(10),
      },
      {
        label: 'Recent dietary change (more/less food, sweets, carbs)',
        value: 'recent dietary change (more/less food, sweets, carbs)',
        key: makeid(10),
      },
      {
        label: 'Physical inactivity or overexertion',
        value: 'physical inactivity or overexertion',
        key: makeid(10),
      },
      {
        label: 'Illness/infection',
        value: 'illness/infection',
        key: makeid(10),
      },
      {
        label: 'Device/measurement issue',
        value: 'device/measurement issue',
        key: makeid(10),
      },
      {
        label: 'No identifiable cause',
        value: 'no identifiable cause',
        key: makeid(10),
      },
    ],
    items: [
      {
        key: makeid(10),
        title: null,
        type: 'textarea',
        name: 'potentialContributingFactorsComment',
      },
    ],
  },
  { ...nurseDecisionMakingNextStepsItem },
  {
    key: makeid(10),
    type: 'checkbox',
    title: <p>Patient education provided:</p>,
    name: 'patientEducationProvided',
    required: false,
    options: [
      {
        label: 'Hypoglycemia action plan',
        value: 'hypoglycemia action plan',
        key: makeid(10),
      },
      {
        label: 'Hyperglycemia action plan',
        value: 'hyperglycemia action plan',
        key: makeid(10),
      },
      {
        label: 'Monitoring technique/when to test',
        value: 'monitoring technique/when to test',
        key: makeid(10),
      },
      {
        label: 'Sick day rules',
        value: 'sick day rules',
        key: makeid(10),
      },
      {
        label: 'Medication/diet adherence',
        value: 'medication/diet adherence',
        key: makeid(10),
      },
      {
        label: 'Warning signs & when to seek help',
        value: 'warning signs & when to seek help',
        key: makeid(10),
      },
    ],
    items: [
      {
        key: makeid(10),
        title: null,
        type: 'textarea',
        name: 'patientEducationProvidedComment',
      },
    ],
  },
  { ...nurseDigitalEngagementEducationMethodItem },
  { ...nurseMaTasksDelegatedItem },
  {
    key: makeid(10),
    type: 'textarea',
    title: <p>Nurse clinical summary and rationale:</p>,
    name: 'nurseClinicalSummaryAndRationale',
    required: false,
  },
  { ...nurseFollowUpPlanItem },
  { ...nursePatientAcknowledgementItem },
  { ...nursePatientAgreeabilityItem },
];
